import { blake3 } from "@noble/hashes/blake3.js";
import { useAuthStore } from "../auth/auth_store";
import { sign } from "../crypto/ed25519";
import { bytesToHex, encSigningCredBare, hexToBytes } from "../crypto/envelope";
import { getCachedDeriveMlDsa } from "../crypto/ml_dsa";
import { DOMAIN_PLAYER_AUTH, DOMAIN_WS_AUTH } from "../types/wire";
import { randomRequestNonceHex } from "./request_nonce";

/**
 * HTTP route authentication header builder. ADR-007 v1.6 — six
 * write-bearing routes require a request-bound ed25519 signature
 * to defend against the spectator-lane mid-hand `join` /
 * `unspectate` / `next-hand` / `join-queue` / `unqueue` /
 * `spectate` impersonation paths the prior session-cookie-only
 * boundary couldn't rule out.
 *
 * Payload layout (byte-for-byte mirror of backend
 * `arkhe-holdem-server::auth::player_auth_payload`):
 *
 *     BLAKE3(DOMAIN_PLAYER_AUTH
 *            || table_id      (16 fixed bytes)
 *            || player_id     (32 fixed bytes — ed25519 verifying key)
 *            || unix_ms_le    (8 little-endian bytes)
 *            || body_hash     (32 fixed bytes — BLAKE3 of request body)
 *            || [epoch_le]    (4 little-endian bytes — rotated cred only)
 *            || request_nonce (16 fixed bytes — FINAL field))
 *
 * The 32-byte digest is then ed25519-signed with the caller's
 * signing seed. The 4 emitted headers are:
 *
 *     x-player-id   : lowercase hex (64 chars)
 *     x-player-sig  : lowercase hex (128 chars)
 *     x-request-ts  : integer unix MILLISECONDS (string)
 *     x-request-nonce: lowercase hex (32 chars — 16 CSPRNG bytes)
 *
 * `x-request-ts` is the canonical wall-clock binding in MILLISECONDS —
 * the server accepts ±300 s skew. It is a plain `Date.now()`: it no
 * longer needs to be the single-use-collision discriminator because the
 * per-request CSPRNG `x-request-nonce` (appended as the payload's FINAL
 * field) makes every legitimate request sign structurally-distinct bytes
 * — two signs in the same physical millisecond (within a tab or across
 * tabs) draw different nonces and so produce different signatures, which
 * the server's single-use replay cache never mistakes for a captured
 * replay. The `body_hash` absorbs the request body verbatim so a captured
 * header set can't be replayed against a different body.
 *
 * The header naming intentionally mirrors the pre-bump
 * `x-player-{id,sig,ts}` convention the auth route already used;
 * the bump only widens the routes that demand the triple, not the
 * scheme itself.
 */

const TEXT_ENCODER = new TextEncoder();

export interface PlayerAuthHeaders {
  readonly "x-player-id": string;
  readonly "x-player-sig": string;
  readonly "x-request-ts": string;
  /** 32 lowercase hex chars (16 CSPRNG bytes) — the per-request nonce
   *  folded into the signed payload as its FINAL field. Always emitted by
   *  this build; the BE dual-accept tolerates its absence from a stale
   *  client. */
  readonly "x-request-nonce": string;
  /** Hex of `postcard(SigningCred)` — present ONLY for an explicit
   *  (rotated ed25519) credential. A genesis ed25519 leg omits it (and so
   *  appends no epoch trailer), even when a hybrid ml-dsa leg is present. */
  readonly "x-signing-cred"?: string;
  /** Decimal u32 string — the active ml_dsa credential epoch hint. Present
   *  only for a hybrid account. */
  readonly "x-ml-dsa-epoch"?: string;
  /** Hex of the 3309-byte ML-DSA-65 signature over the SAME auth payload
   *  the ed25519 leg signs. Present only for a hybrid account. */
  readonly "x-ml-dsa-sig"?: string;
}

/**
 * Optional credential legs shared by the table-scoped + account-self
 * header builders. ABSENT entirely => genesis (today's clients): no
 * `x-signing-cred`, no ml-dsa headers, no epoch trailer.
 */
export interface AuthCredentialLegs {
  /** The ed25519 credential epoch. When `> 0` the builder emits
   *  `x-signing-cred` + binds the epoch trailer. When `0` / omitted the
   *  ed25519 leg is genesis — NO `x-signing-cred`, NO trailer (CRITICAL:
   *  even for a hybrid account, the trailer keys off THIS, never ml-dsa). */
  readonly ed25519Epoch?: number;
  /** Hybrid second leg: the 32-byte ML-DSA-65 SEED. Present => emit the
   *  `x-ml-dsa-epoch` + `x-ml-dsa-sig` headers. */
  readonly mlDsaSeed?: Uint8Array | null;
  /** The ml_dsa credential epoch hint (DB-current). */
  readonly mlDsaEpoch?: number | null;
}

/**
 * Read the active credential legs out of the in-memory auth slice. This is
 * the SINGLE source that the WS handshake (`ws/client.ts`) reads through its
 * `options.{ed25519Epoch, mlDsaSeed, mlDsaEpoch}` wiring; the HTTP signers
 * default to this same read so every authed surface emits the same legs as
 * the WS lane without each of the ~12 call sites threading the creds by hand.
 *
 * A genesis account (`ed25519Epoch === 0`, `mlDsaSeed === null`) resolves to
 * legs that emit NOTHING — no `x-signing-cred`, no ml-dsa headers, no epoch
 * trailer — so a pre-cutover / non-hybrid client stays byte-identical. A
 * rotated account surfaces `ed25519Epoch > 0` (=> `x-signing-cred` + the
 * epoch-bound message); a hybrid account surfaces `mlDsaSeed` (=> the ml-dsa
 * second leg). The seed buffer is passed by reference (never copied / logged).
 */
export function authStoreCredentialLegs(): AuthCredentialLegs {
  const s = useAuthStore.getState();
  return {
    ed25519Epoch: s.ed25519Epoch,
    mlDsaSeed: s.mlDsaSeed,
    mlDsaEpoch: s.mlDsaEpoch,
  };
}

export interface SignPlayerAuthHeadersArgs {
  /** Lowercase hex of the 16-byte in-memory table id. */
  readonly tableIdHex: string;
  /** Lowercase hex of the 32-byte ed25519 verifying key bound to
   *  the account row. */
  readonly playerIdHex: string;
  /** 32-byte ed25519 signing seed (the in-memory auth-store key). */
  readonly signingKey: Uint8Array;
  /** Request body the caller will send. Used as-is for the body
   *  hash binding — pass the exact serialized JSON / form bytes,
   *  not a parsed object. `""` is valid for empty-body POSTs. */
  readonly body: string;
  /** Hybrid / rotated credential legs. OMITTED (undefined) => default-read
   *  the active legs from the auth slice (`authStoreCredentialLegs`) so the
   *  ~12 callers stay caller-free; an EXPLICIT value (incl. `{}` for genesis)
   *  overrides the read — used by the enroll path that must prove ownership
   *  with the genesis key only, and by vitest for pinned-leg cases. */
  readonly creds?: AuthCredentialLegs;
  /** Optional clock override for vitest. Returns unix MILLISECONDS
   *  (Date.now() by default). */
  readonly now?: () => number;
}

/**
 * Build the optional `{ x-signing-cred, x-ml-dsa-epoch, x-ml-dsa-sig }`
 * header trio + the epoch-binding trailer value for a set of credential
 * legs. Returns `signingEpoch === undefined` for the genesis ed25519 path
 * (so the auth payload appends NO trailer). The ml-dsa sig is computed by
 * the caller (it signs the canonical payload AFTER `signingEpoch` is known)
 * — this helper only produces the `x-signing-cred` + the trailer decision.
 */
export function buildSigningCredHeader(
  playerId: Uint8Array,
  creds: AuthCredentialLegs | undefined,
): { signingCredHeader?: string; signingEpoch?: number } {
  const edEpoch = creds?.ed25519Epoch ?? 0;
  if (edEpoch <= 0) {
    // Genesis ed25519 leg — no x-signing-cred, no trailer (even hybrid).
    return {};
  }
  return {
    signingCredHeader: bytesToHex(
      encSigningCredBare({ epoch: edEpoch, kind: "ed25519", pubkey: playerId }),
    ),
    signingEpoch: edEpoch,
  };
}

function u64LE(n: bigint): Uint8Array {
  if (n < 0n || n > (1n << 64n) - 1n) {
    throw new RangeError(`u64LE: out of range: ${n}`);
  }
  const buf = new Uint8Array(8);
  let v = n;
  for (let i = 0; i < 8; i++) {
    buf[i] = Number(v & 0xffn);
    v >>= 8n;
  }
  return buf;
}

/** Fixed 4-byte little-endian u32. The BE epoch-binding trailer
 *  (`epoch.to_le_bytes()`) is exactly 4 bytes — NOT a varint. */
function u32LE(n: number): Uint8Array {
  if (!Number.isInteger(n) || n < 0 || n > 0xffffffff) {
    throw new RangeError(`u32LE: out of u32 range: ${n}`);
  }
  const buf = new Uint8Array(4);
  buf[0] = n & 0xff;
  buf[1] = (n >>> 8) & 0xff;
  buf[2] = (n >>> 16) & 0xff;
  buf[3] = (n >>> 24) & 0xff;
  return buf;
}

function concatBytes(...chunks: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

/**
 * Compute the canonical 32-byte BLAKE3 signing payload for an
 * HTTP route auth header triple. Exported so vitest can pin the
 * byte layout against the Rust mirror without going through the
 * signing path.
 */
export function playerAuthPayload(args: {
  tableIdHex: string;
  playerIdHex: string;
  unixMs: number;
  body: string;
  /** Epoch-binding trailer — appended as `u32LE(epoch)` AFTER `body_hash`
   *  ONLY when the client presents an EXPLICIT credential (a rotated
   *  ed25519 cred). `undefined` => genesis path: NO trailer, so the
   *  payload is bit-for-bit the legacy bytes today's clients produce.
   *  CRITICAL: this is the ED25519 cred epoch, NEVER the ml-dsa epoch. */
  signingEpoch?: number;
  /** Per-request nonce — 32 hex chars (16 CSPRNG bytes) appended as the
   *  FINAL field, AFTER any epoch trailer. `undefined` => no append, so
   *  the payload is bit-for-bit the legacy bytes (mirrors the BE
   *  `request_nonce: None` dual-accept branch). */
  requestNonceHex?: string;
}): Uint8Array {
  const tableId = hexToBytes(args.tableIdHex);
  const playerId = hexToBytes(args.playerIdHex);
  if (tableId.length !== 16) {
    throw new RangeError(
      `playerAuthPayload: tableId must be 16 bytes, got ${tableId.length}`,
    );
  }
  if (playerId.length !== 32) {
    throw new RangeError(
      `playerAuthPayload: playerId must be 32 bytes, got ${playerId.length}`,
    );
  }
  const bodyBytes = TEXT_ENCODER.encode(args.body);
  const bodyHash = blake3(bodyBytes, { dkLen: 32 });
  const chunks: Uint8Array[] = [
    DOMAIN_PLAYER_AUTH,
    tableId,
    playerId,
    u64LE(BigInt(args.unixMs)),
    bodyHash,
  ];
  if (args.signingEpoch !== undefined) {
    chunks.push(u32LE(args.signingEpoch));
  }
  if (args.requestNonceHex !== undefined) {
    const nonce = hexToBytes(args.requestNonceHex);
    if (nonce.length !== 16) {
      throw new RangeError(
        `playerAuthPayload: requestNonce must be 16 bytes, got ${nonce.length}`,
      );
    }
    chunks.push(nonce);
  }
  return blake3(concatBytes(...chunks), { dkLen: 32 });
}

/**
 * Build the `{ "x-player-id", "x-player-sig", "x-request-ts" }`
 * triple for a route-authenticated POST. The caller passes the
 * exact body string they're about to send so the body-hash
 * binding is correct — drift between this helper's body and the
 * actual request body would surface as a server-side 401.
 */
export function signPlayerAuthHeaders(
  args: SignPlayerAuthHeadersArgs,
): PlayerAuthHeaders {
  if (args.signingKey.length !== 32) {
    throw new RangeError(
      `signPlayerAuthHeaders: signingKey must be 32 bytes, got ${args.signingKey.length}`,
    );
  }
  // Plain wall clock: the per-request nonce (below) is the single-use
  // discriminator now, so `unix_ms` only has to land inside the server's
  // ±300 s skew window. An explicit `now` (the vitest pin) overrides.
  const unixMs = args.now !== undefined ? args.now() : Date.now();
  // Fresh CSPRNG nonce per call — folded into the payload as its FINAL
  // field + emitted as `x-request-nonce`. Two signs in the same physical
  // ms (within a tab, or across tabs of the same account) draw distinct
  // nonces => distinct signatures, so neither false-trips the server's
  // single-use replay cache. This subsumes the prior monotonic-clock /
  // per-tab-body-nonce scaffolding.
  const requestNonceHex = randomRequestNonceHex();
  const playerId = hexToBytes(args.playerIdHex);
  // OMITTED `creds` => default-read the active legs from the auth slice so
  // every HTTP caller emits the same legs the WS lane does; an explicit
  // value (incl. `{}`) overrides (enroll genesis-only path / vitest).
  const creds = args.creds ?? authStoreCredentialLegs();
  const { signingCredHeader, signingEpoch } = buildSigningCredHeader(
    playerId,
    creds,
  );
  // Both legs sign this SAME nonced + epoch-bound digest. The trailer is
  // keyed off the ed25519 cred epoch (via `signingEpoch`), never the
  // ml-dsa epoch; the nonce is the FINAL field after any trailer.
  const payload = playerAuthPayload({
    tableIdHex: args.tableIdHex,
    playerIdHex: args.playerIdHex,
    unixMs,
    body: args.body,
    requestNonceHex,
    ...(signingEpoch !== undefined ? { signingEpoch } : {}),
  });
  const sig = sign(payload, args.signingKey);
  const headers: Record<string, string> = {
    "x-player-id": args.playerIdHex,
    "x-player-sig": bytesToHex(sig),
    "x-request-ts": String(unixMs),
    "x-request-nonce": requestNonceHex,
  };
  if (signingCredHeader !== undefined) {
    headers["x-signing-cred"] = signingCredHeader;
  }
  if (creds.mlDsaSeed != null) {
    // Hybrid leg — read the boot-preloaded ml-dsa leaf so this builder
    // stays synchronous (its ~9 callers pass no `await`).
    const mlSig = getCachedDeriveMlDsa()(creds.mlDsaSeed).sign(payload);
    headers["x-ml-dsa-epoch"] = String(creds.mlDsaEpoch ?? 0);
    headers["x-ml-dsa-sig"] = bytesToHex(mlSig);
  }
  return headers as unknown as PlayerAuthHeaders;
}

/**
 * Compute the BLAKE3 challenge payload the WS handshake signs
 * with. Mirror of backend `arkhe-holdem-server::ws_auth::
 * sign_payload`. The 5-frame WS dance feeds the result into
 * `ed25519.sign` and replies with an `auth_response` ClientCommand
 * (see `ws/client.ts` for the handshake state machine + this
 * helper's primary consumer).
 *
 *     BLAKE3(DOMAIN_WS_AUTH
 *            || table_id          (16 fixed bytes)
 *            || nonce             (32 fixed bytes, server-issued)
 *            || server_unix_ms_le (8 little-endian bytes))
 *
 * The `nonce` is the verbatim 32 bytes the server sent — the
 * hex-on-wire form is parsed back to bytes by the WS client
 * before this helper runs.
 */
export function wsAuthChallengePayload(args: {
  tableIdHex: string;
  nonceHex: string;
  serverUnixMs: number;
  /** Epoch-binding trailer — appended as `u32LE(epoch)` ONLY when the
   *  client presents an EXPLICIT (rotated ed25519) credential. `undefined`
   *  => genesis path: NO trailer (bit-for-bit legacy). The ED25519 cred
   *  epoch, NEVER the ml-dsa epoch. */
  signingEpoch?: number;
}): Uint8Array {
  const tableId = hexToBytes(args.tableIdHex);
  const nonce = hexToBytes(args.nonceHex);
  if (tableId.length !== 16) {
    throw new RangeError(
      `wsAuthChallengePayload: tableId must be 16 bytes, got ${tableId.length}`,
    );
  }
  if (nonce.length !== 32) {
    throw new RangeError(
      `wsAuthChallengePayload: nonce must be 32 bytes, got ${nonce.length}`,
    );
  }
  const chunks: Uint8Array[] = [
    DOMAIN_WS_AUTH,
    tableId,
    nonce,
    u64LE(BigInt(args.serverUnixMs)),
  ];
  if (args.signingEpoch !== undefined) {
    chunks.push(u32LE(args.signingEpoch));
  }
  return blake3(concatBytes(...chunks), { dkLen: 32 });
}
